import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useCountUp from '@/hooks/useCountUp';

interface InfoItem {
  icon: string;
  valueKey: string;
  labelKey: string;
  suffix?: string;
  prefix?: string;
  duration?: number;
}

const infoItems: InfoItem[] = [
  { icon: 'ri-building-line', valueKey: 'home.info.area.value', labelKey: 'home.info.area.label', suffix: 'm²', duration: 2000 },
  { icon: 'ri-trophy-line', valueKey: 'home.info.classes.value', labelKey: 'home.info.classes.label', suffix: '+', duration: 1800 },
  { icon: 'ri-time-line', valueKey: 'home.info.hours.value', labelKey: 'home.info.hours.label', suffix: 'h', duration: 1600 },
  { icon: 'ri-sun-line', valueKey: 'home.info.beach.value', labelKey: 'home.info.beach.label', suffix: 'm', duration: 2200 },
];

function CounterItem({ item, isVisible }: { item: InfoItem; isVisible: boolean }) {
  const { t } = useTranslation();
  const value = parseInt(t(item.valueKey), 10) || 0;

  const displayValue = useCountUp({
    end: value,
    duration: item.duration || 2000,
    enabled: isVisible,
    suffix: item.suffix || '',
    prefix: item.prefix || '',
  });

  return (
    <div className="reveal-item reveal flex flex-col items-center text-center gap-1">
      <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-primary-500">
        <i className={`${item.icon} text-2xl md:text-3xl`}></i>
      </div>
      <span className="font-heading text-3xl md:text-4xl text-background-50 leading-none">
        {displayValue}
      </span>
      <span className="font-body text-xs md:text-sm text-background-400 mt-1">
        {t(item.labelKey)}
      </span>
    </div>
  );
}

export default function InfoBar() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const items = el.querySelectorAll('.reveal-item');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            const index = Array.from(items).indexOf(target);
            target.style.transitionDelay = `${index * 100}ms`;
            target.classList.add('reveal-visible');
            observer.unobserve(target);
          }
        });
        setIsVisible(true);
      },
      { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-foreground-950 py-8 md:py-10">
      <div className="w-full px-4 md:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {infoItems.map((item) => (
            <CounterItem key={item.valueKey} item={item} isVisible={isVisible} />
          ))}
        </div>
      </div>
    </section>
  );
}